// Hook React Query per operazioni HSE: certifications, safety documents, upload, download, invio email
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'

// ===== TIPI =====

export interface Certification {
  id: string
  technicianId: string
  type: string
  certificateNumber?: string
  issuer?: string
  issueDate?: string
  expiryDate?: string
  filePath?: string
  fileName?: string
  fileSize?: number
  mimeType?: string
  isVerified?: boolean
  status?: string
}

export interface SafetyDocument {
  id: string
  type: string
  title: string
  companyId?: string
  siteId?: string
  issueDate?: string
  expiryDate?: string
  filePath?: string
  fileName?: string
  fileSize?: number
  mimeType?: string
  status?: string
}

export interface SendLog {
  id: string
  sentBy: string
  recipientEmail: string
  subject: string
  deliveryMode: 'attachment' | 'link' | 'both'
  documents: Array<{ type: string; id: string; fileName: string }>
  sentAt: string
}

export interface SendDocumentsPayload {
  recipientEmail: string
  subject: string
  deliveryMode: 'attachment' | 'link' | 'both'
  documents: Array<{ type: 'certification' | 'safety_doc'; id: string }>
}

// ===== QUERY KEYS =====

export const hseKeys = {
  certifications: (filters?: Record<string, unknown>) =>
    ['hse', 'certifications', filters ?? {}] as const,
  documents: () => ['hse', 'documents'] as const,
  sendLogs: (filters?: Record<string, unknown>) =>
    ['hse', 'send-logs', filters ?? {}] as const,
}

// ===== CERTIFICAZIONI =====

export function useCertifications(filters?: { technicianId?: string }) {
  return useQuery({
    queryKey: hseKeys.certifications(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await api.get<Certification[]>('/hse/certifications', { params: filters })
      return res.data
    },
  })
}

// ===== DOCUMENTI AZIENDA =====

export function useSafetyDocuments() {
  return useQuery({
    queryKey: hseKeys.documents(),
    queryFn: async () => {
      const res = await api.get<SafetyDocument[]>('/hse/documents')
      return res.data
    },
  })
}

// ===== UPLOAD =====

export function useUploadCertification(certId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post<Certification>(`/hse/certifications/${certId}/upload`, form)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hse', 'certifications'] })
    },
  })
}

export function useUploadDocument(docId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post<SafetyDocument>(`/hse/documents/${docId}/upload`, form)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: hseKeys.documents() })
    },
  })
}

// ===== DOWNLOAD (presigned URL) =====

export function useDownloadCertification() {
  return useMutation({
    mutationFn: async (certId: string) => {
      const res = await api.get<{ url: string; expiresAt: string }>(
        `/hse/certifications/${certId}/download`,
      )
      return res.data
    },
  })
}

export function useDownloadDocument() {
  return useMutation({
    mutationFn: async (docId: string) => {
      const res = await api.get<{ url: string; expiresAt: string }>(
        `/hse/documents/${docId}/download`,
      )
      return res.data
    },
  })
}

// ===== INVIO EMAIL =====

export function useSendDocuments() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: SendDocumentsPayload) => {
      const res = await api.post<{ ok: boolean; logId: string; sentAt: string }>(
        '/hse/send-documents',
        payload,
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hse', 'send-logs'] })
    },
  })
}

// ===== STORICO INVII =====

export function useSendLogs(limit = 10) {
  return useQuery({
    queryKey: hseKeys.sendLogs({ limit }),
    queryFn: async () => {
      const res = await api.get<{ items: SendLog[]; total: number }>('/hse/send-logs', {
        params: { limit },
      })
      return res.data
    },
  })
}
